import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { HiddenIframe } from '@/components/HiddenIframe/HiddenIframe';
import { Context } from '@/components/Store';
import { ActionTypes, type State } from '@/components/Store.d';
import { config } from '@/lib/config';
import { getErrorCodeFromMessage } from '@/lib/errorMessages';
import { useMessage, useNavigate } from '@/lib/hooks';
import * as http from '@/lib/http';
import { error as errorLog, log } from '@/lib/log';
import { hasChallengeRequiredV2, isSinglePayment } from '@/lib/util';

import type { CompleteAsyncPurchaseData, PaypalPurchaseParams } from './Approve.d';

const Approve = () => {
	const [state, dispatch] = useContext(Context);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [hiddenIframeProps, setHiddenIframeProps] = useState({
		show: false,
		type: '',
		actionUrl: '',
		value: ''
	});

	const allowedOrigin =
		import.meta.env.VITE_APP_ENVIRONMENT === 'production'
			? 'https://payments.mppglobal.com'
			: 'https://paymentsuat.mppglobal.com';

	useMessage(onMessageReceive);

	/**
	 * Updates payment method in state after successful purchase
	 * Only updates if the user chose to save the payment method
	 */
	function updatePaymentMethodState() {
		if (state.payment?.reusePaymentMethod && state.payment?.paymentMethod) {
			dispatch({
				type: ActionTypes.setMovableSubscription,
				payload: {
					...state.upgrade,
					activePaymentMethod: state.payment.paymentMethod
				}
			});
		}
	}

	useEffect(() => {
		const sessionState: State = JSON.parse(sessionStorage.getItem('sessionData') || '{}');

		if (sessionState) {
			dispatch({
				type: ActionTypes.restoreFromStorage,
				payload: sessionState
			});
		}
	}, [dispatch]);

	useEffect(() => {
		if (state.restoredFromStorage !== true) {
			return;
		}

		if (searchParams.has('paypalresponse') && searchParams.has('token')) {
			handlePaypalResponse();
			return;
		}

		if (searchParams.has('PASREF') && searchParams.has('SHA1HASH') && searchParams.has('ORDERID')) {
			completeSofortPurchase(Object.fromEntries(searchParams.entries()));
			return;
		}

		if (searchParams.get('creq') === '2') {
			setHiddenIframeProps({
				show: true,
				type: 'challengeV2',
				actionUrl: state.challengeRequired.threeDSecureChallengeRequestUrl as string,
				value: (state.challengeRequired.threeDSecureEncodedCreq as string) || ''
			});
			return;
		}

		if (state.payment?.paymentMethod === 'creditcard') {
			completeCreditcardPurchase();
			return;
		}
		// This effect should only run once when state is restored from storage
		// Functions are intentionally not in deps to avoid re-running
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.restoredFromStorage, dispatch]);

	/**
	 * Function to handle the redirect from paypal
	 */
	function handlePaypalResponse() {
		if (searchParams.get('paypalresponse') === 'authorised') {
			completePaypalPurchase(
				Object.fromEntries(searchParams.entries()) as unknown as PaypalPurchaseParams
			);
		} else {
			errorLog('PayPal purchase failed', {
				status: searchParams.get('paypalresponse')
			});
			navigate(config.routes.error, { searchParams: { code: 'paypalCancelled' } });
		}
	}

	/**
	 * Callback function for receiving messages
	 * Only needed to handle 3DSecure challenges for creditcard payments
	 * @param {Event} e
	 */
	function onMessageReceive(e: MessageEvent) {
		log(`Approve: Receiving message from hidden iframe ${e.origin}`, e.data);

		if (state.payment?.paymentMethod !== 'creditcard') {
			return;
		}

		if (e.origin === allowedOrigin) {
			log('Approve: Removing event listener for hidden iframe');

			try {
				completeCreditcardPurchase();
			} catch (error) {
				errorLog(error);
				const code = getErrorCodeFromMessage(error instanceof Error ? error.message : undefined);
				log(
					'An error occured executing completeCreditcardPurchase; Changed route to',
					config.routes.error
				);
				navigate(config.routes.error, { searchParams: { code: code ?? undefined } });
			}
		}
	}

	/**
	 * Completes Paypal purchase
	 * @param {PaypalPurchaseParams} params
	 */
	function completePaypalPurchase(params: PaypalPurchaseParams) {
		const data: CompleteAsyncPurchaseData = {
			sessionToken: state?.payment?.sessionToken,
			isSinglePayment: isSinglePayment(state),
			asyncProcessingParameters: {
				paypalresponse: params.paypalresponse,
				token: params.token,
				PayerID: params.PayerID || undefined
			},
			moveFromSubscriptionReference: state?.upgrade?.moveFromSubscriptionRef || undefined,
			contractReference: state?.bundle?.contractReference
		};

		http.completeAsyncPurchase(data, state?.user?.tokenID || '').then(
			(result) => {
				updatePaymentMethodState();

				dispatch({
					type: ActionTypes.purchaseCompleted,
					payload: result
				});

				log('Change route to', config.routes.confirmation);
				navigate(config.routes.confirmation, { replace: true });
			},
			(error) => {
				errorLog(error);
				const code = getErrorCodeFromMessage(error?.message);
				log('Change route to', config.routes.error);
				navigate(config.routes.error, { searchParams: { code: code ?? undefined }, replace: true });
			}
		);
	}

	/**
	 * Comletes Sofort purchase
	 * @param {object} params
	 */
	function completeSofortPurchase(params: object) {
		const data = {
			sessionId: state?.payment?.sessionToken,
			isSinglePayment: isSinglePayment(state),
			countryCode: state.address?.delivery?.country || state.upgrade?.activeCountryCode,
			asynchronousProcessingParameters: {
				...params,
				STAGE: 'COMPLETE'
			}
		};

		http.completeSofort(data, state?.user?.tokenID || '').then(
			(result) => {
				const order = {
					order: {
						orderReference: result?.orders[0]?.orderId
					}
				};

				updatePaymentMethodState();

				dispatch({
					type: ActionTypes.purchaseCompleted,
					payload: order
				});

				log('Change route to', config.routes.confirmation);
				navigate(config.routes.confirmation);
			},
			(error) => {
				errorLog(error);
				const code = getErrorCodeFromMessage(error?.message);
				log('Change route to', config.routes.error);
				navigate(config.routes.error, { searchParams: { code: code ?? undefined } });
			}
		);
	}

	/**
	 * Completes Credit Card purchase
	 */
	function completeCreditcardPurchase() {
		const searchParams = new URLSearchParams(window.location.search);
		const queryStrings = Object.fromEntries(searchParams.entries());

		const data: CompleteAsyncPurchaseData = {
			sessionToken: state?.payment?.sessionToken,
			asyncProcessingParameters: {
				cvv: null,
				paymentMethod: 'CreditCard',
				voucherCode: null,
				MD: queryStrings.MD,
				PaRes: queryStrings.PaRes
			},
			isSinglePayment: isSinglePayment(state),
			moveFromSubscriptionReference: state?.upgrade?.moveFromSubscriptionRef,
			contractReference: state?.bundle?.contractReference
		};

		http
			.completeAsyncPurchase(data, state?.user?.tokenID || '')
			.then((result) => {
				if (hasChallengeRequiredV2(result)) {
					setHiddenIframeProps({
						show: true,
						type: 'challengeV2',
						actionUrl: result.asynchronousProcessingParameters.threeDSecureChallengeRequestUrl,
						value: result.asynchronousProcessingParameters.threeDSecureEncodedCreq || ''
					});
				} else {
					updatePaymentMethodState();

					dispatch({
						type: ActionTypes.purchaseCompleted,
						payload: result
					});

					log('Change route to', config.routes.confirmation);
					navigate(config.routes.confirmation, { replace: true });
				}
			})
			.catch((error) => {
				errorLog(error);
				const code = getErrorCodeFromMessage(error?.message);
				log('Change route to', config.routes.error);
				navigate(config.routes.error, { searchParams: { code: code ?? undefined }, replace: true });
			});
	}

	return (
		<>
			<div>Bitte haben Sie noch einen Moment Geduld.</div>
			{hiddenIframeProps.show ? (
				<HiddenIframe
					actionUrl={hiddenIframeProps.actionUrl}
					type={hiddenIframeProps.type}
					value={hiddenIframeProps.value}
				/>
			) : null}
		</>
	);
};

export { Approve };
